import React, {useState} from 'react';
import {Box, VStack, Input, Checkbox, useTheme, InputGroup, InputRightElement, IconButton, Image} from '@chakra-ui/react';
import {TextG12, TextG14, TextG24} from '../../components/typography';
import ExternalLink from '../../components/ExternalLink';
import ShowPassword from '../../assets/images/eye.png';
import logger from '../../utils/logger';

export const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[~`!@#$%^&*()_\-+={[}]|\:;"'<,>.?\/])[a-zA-Z\d~`!@#$%^&*()_\-+={[}]|\:;"'<,>.?\/]{8,}$/;

interface Intro5FormProps {
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    wantsEmails: boolean;
  }) => void;
  initialData?: {firstName: string; lastName: string; email: string; password: string; wantsEmails: boolean};
}

const IntroAccountInfo = ({onValidationChange, onDataChange, initialData}: Intro5FormProps) => {
  const theme = useTheme();
  const [firstName, setFirstName] = useState(initialData?.firstName || '');
  const [lastName, setLastName] = useState(initialData?.lastName || '');
  const [email, setEmail] = useState(initialData?.email || '');
  const [password, setPassword] = useState(initialData?.password || '');
  const [wantsEmails, setWantsEmails] = useState(initialData?.wantsEmails || false);
  const [showPassword, setShowPassword] = useState(false);
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    password: false,
  });

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const errors = {
    firstName:
      touched.firstName && firstName.length > 0 && firstName.length < 3
        ? 'First name must be at least 3 characters'
        : '',
    lastName:
      touched.lastName && lastName.length > 0 && lastName.length < 3
        ? 'Last name must be at least 3 characters'
        : '',
    email:
      touched.email && email.length > 0 && !validateEmail(email)
        ? 'Please enter a valid email address'
        : '',
    password:
      touched.password && password.length > 0 && !PASSWORD_REGEX.test(password)
        ? 'Password requires minimum 8 characters with uppercase, lowercase, numbers and symbols'
        : '',
  };

  const isValid =
    firstName.length >= 3 && lastName.length >= 3 && email.length > 0 && validateEmail(email) && PASSWORD_REGEX.test(password);

  // Diagnostic logging for validation state (CT-5853)
  React.useEffect(() => {
    logger.debug('IntroAccountInfo validation', 'user_action', {
      firstName: firstName.length >= 3,
      lastName: lastName.length >= 3,
      email: email.length > 0 && validateEmail(email),
      password: PASSWORD_REGEX.test(password),
      isValid,
    });
    onValidationChange(isValid);
    onDataChange({firstName, lastName, email, password, wantsEmails});
  }, [firstName, lastName, email, password, wantsEmails, isValid]);

  // Fix: Ensure validation fires on initial mount with pre-filled data
  React.useEffect(() => {
    if (initialData) {
      const initialValid =
        (initialData.firstName?.length ?? 0) >= 3 &&
        (initialData.lastName?.length ?? 0) >= 3 &&
        (initialData.email?.length ?? 0) > 0 &&
        validateEmail(initialData.email || '') &&
        PASSWORD_REGEX.test(initialData.password || '');
      if (initialValid) {
        logger.debug('IntroAccountInfo: Initial data is valid, triggering validation', 'user_action', {});
        onValidationChange(true);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <VStack
      flex={1}
      spacing={6}
      px={4}
      pt={4}
      pb={4}
      align="stretch"
      justify="flex-start"
      overflow="auto">
      {/* Title */}
      <Box>
        <TextG24 fontSize="32px" fontWeight="700" color="#262626">
          Hi Neighbor! 👋
        </TextG24>
        <TextG24 fontSize="32px" fontWeight="700" color="#262626">
          What's your name?
        </TextG24>
      </Box>

      {/* Form Fields */}
      <VStack spacing={4} align="stretch">
        {/* First Name */}
        <Box>
          <Input
            placeholder="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onBlur={() => setTouched({...touched, firstName: true})}
            size="lg"
            borderRadius="12px"
            border="1px solid"
            borderColor={errors.firstName ? theme.colors.red[500] : theme.colors.gray[300]}
            _focus={{
              borderColor: errors.firstName ? theme.colors.red[500] : '#C9136B',
              boxShadow: 'none',
            }}
            fontSize="16px"
            py={6}
          />
          {errors.firstName && (
            <TextG14 color={theme.colors.red[500]} mt={1} ml={2}>
              {errors.firstName}
            </TextG14>
          )}
        </Box>

        {/* Last Name */}
        <Box>
          <Input
            placeholder="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            onBlur={() => setTouched({...touched, lastName: true})}
            size="lg"
            borderRadius="12px"
            border="1px solid"
            borderColor={errors.lastName ? theme.colors.red[500] : theme.colors.gray[300]}
            _focus={{
              borderColor: errors.lastName ? theme.colors.red[500] : '#C9136B',
              boxShadow: 'none',
            }}
            fontSize="16px"
            py={6}
          />
          {errors.lastName && (
            <TextG14 color={theme.colors.red[500]} mt={1} ml={2}>
              {errors.lastName}
            </TextG14>
          )}
        </Box>

        {/* Email */}
        <Box>
          <Input
            placeholder="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched({...touched, email: true})}
            size="lg"
            borderRadius="12px"
            border="1px solid"
            borderColor={errors.email ? theme.colors.red[500] : theme.colors.gray[300]}
            _focus={{
              borderColor: errors.email ? theme.colors.red[500] : '#C9136B',
              boxShadow: 'none',
            }}
            fontSize="16px"
            py={6}
          />
          {errors.email && (
            <TextG14 fontSize="13px" color={theme.colors.red[500]} mt={1} ml={2}>
              {errors.email}
            </TextG14>
          )}
        </Box>

        {/* Password */}
        <Box>
          <InputGroup size="lg">
            <Input
              placeholder="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setTouched({...touched, password: true})}
              borderRadius="12px"
              border="1px solid"
              borderColor={errors.password ? theme.colors.red[500] : theme.colors.gray[300]}
              _focus={{
                borderColor: errors.password ? theme.colors.red[500] : '#C9136B',
                boxShadow: 'none',
              }}
              fontSize="16px"
              py={6}
            />
            <InputRightElement h="full">
              <Image
                src={ShowPassword}
                alt={showPassword ? 'Hide password' : 'Show password'}
                cursor="pointer"
                onClick={() => setShowPassword(!showPassword)}
              />
            </InputRightElement>
          </InputGroup>
          {errors.password && (
            <TextG14 fontSize="13px" color={theme.colors.red[500]} mt={1} ml={2}>
              {errors.password}
            </TextG14>
          )}
        </Box>

        {/* Email Usage Text */}
        <TextG14 color="#262626" fontWeight="500" lineHeight="22px" mt={-2}>
          We'll use this to send you Local Guide emails.
        </TextG14>

        {/* Checkbox */}
        <Checkbox
          isChecked={wantsEmails}
          onChange={(e) => setWantsEmails(e.target.checked)}
          colorScheme="pink"
          size="md"
          spacing={3}>
          <TextG14 color="#262626" fontWeight="500" lineHeight="22px">
            Want emails with updates on your neighborhood?
          </TextG14>
        </Checkbox>

        {/* Terms & Conditions */}
        <TextG12 fontSize="12px" color={theme.colors.gray[600]} textAlign="center" mt={2}>
          By continuing, you agree with our{' '}
          <ExternalLink
            href={`${process.env.CONDITIONS_WEB_PAGE_URL}/terms-of-use/`}
            color="#C9136B"
            textDecoration="underline">
            Terms & Conditions
          </ExternalLink>{' '}
          and{' '}
          <ExternalLink
            href={`${process.env.CONDITIONS_WEB_PAGE_URL}/privacy-policy/`}
            color="#C9136B"
            textDecoration="underline">
            Privacy Statement
          </ExternalLink>
          .
        </TextG12>
      </VStack>
    </VStack>
  );
};

export default IntroAccountInfo;
